import React, { Component } from 'react';
import { graphql, compose, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import { Megaphone, Plus, TrendingUp, MousePointerClick, Eye, DollarSign, X, Upload } from 'lucide-react';

import s from './AppAds.css';

// GraphQL Queries
const GET_APP_ADS = gql`
  query GetAppAds {
    getAppAds {
      id
      title
      description
      imageUrl
      targetUrl
      placement
      locationId
      priority
      isActive
      startDate
      endDate
      advertiserName
      advertiserLogo
      categoryType
      budget
      costPerClick
      costPerImpression
      createdAt
      totalImpressions
      totalClicks
      ctr
      totalSpend
      isScheduleActive
    }
  }
`;

const GET_ANALYTICS = gql`
  query GetAppAdAnalytics($startDate: String, $endDate: String) {
    getAppAdAnalytics(startDate: $startDate, endDate: $endDate) {
      totalImpressions
      totalClicks
      averageCtr
      totalRevenue
      dailyStats {
        date
        impressions
        clicks
        ctr
      }
      topAdsByCtr {
        adId
        title
        impressions
        clicks
        ctr
      }
      platformBreakdown {
        platform
        impressions
        clicks
        percentage
      }
    }
  }
`;

const GET_POPULAR_LOCATIONS = gql`
  query GetPopularLocation {
    getPopularLocation {
      id
      location
    }
  }
`;

class AppAds extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeTab: 'campaigns',
            showModal: false,
            editingAd: null,
            message: null,
            messageType: null,
            form: this.getEmptyForm(),
            uploadingImage: false,
            uploadingLogo: false,
        };
    }

    getEmptyForm() {
        return {
            title: '',
            description: '',
            imageUrl: '',
            targetUrl: '',
            placement: 'home_explore',
            locationId: '',
            priority: 0,
            isActive: true,
            startDate: '',
            endDate: '',
            advertiserName: '',
            advertiserLogo: '',
            categoryType: 'all',
            budget: 0,
            costPerClick: 0,
            costPerImpression: 0,
        };
    }

    showMessage = (text, type = 'success') => {
        this.setState({ message: text, messageType: type });
        setTimeout(() => this.setState({ message: null }), 4000);
    };

    openCreateModal = () => {
        this.setState({
            showModal: true,
            editingAd: null,
            form: this.getEmptyForm(),
        });
    };

    openEditModal = (ad) => {
        this.setState({
            showModal: true,
            editingAd: ad.id,
            form: {
                title: ad.title || '',
                description: ad.description || '',
                imageUrl: ad.imageUrl || '',
                targetUrl: ad.targetUrl || '',
                placement: ad.placement || 'home_explore',
                locationId: ad.locationId || '',
                priority: ad.priority || 0,
                isActive: ad.isActive,
                startDate: ad.startDate ? ad.startDate.split('T')[0] : '',
                endDate: ad.endDate ? ad.endDate.split('T')[0] : '',
                advertiserName: ad.advertiserName || '',
                advertiserLogo: ad.advertiserLogo || '',
                categoryType: ad.categoryType || 'all',
                budget: ad.budget || 0,
                costPerClick: ad.costPerClick || 0,
                costPerImpression: ad.costPerImpression || 0,
            },
        });
    };

    closeModal = () => {
        this.setState({ showModal: false, editingAd: null });
    };

    handleFormChange = (field, value) => {
        this.setState(prev => ({
            form: { ...prev.form, [field]: value }
        }));
    };

    handleImageUpload = async (field, file) => {
        const loadingKey = field === 'imageUrl' ? 'uploadingImage' : 'uploadingLogo';
        this.setState({ [loadingKey]: true });

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch('/uploadAppAd', {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            const result = await response.json();

            if (result.file && result.file.filename) {
                const imageUrl = '/images/appads/' + result.file.filename;
                this.handleFormChange(field, imageUrl);
            } else {
                this.showMessage('Error al subir imagen', 'error');
            }
        } catch (err) {
            this.showMessage('Error al subir imagen: ' + err.message, 'error');
        } finally {
            this.setState({ [loadingKey]: false });
        }
    };

    renderImageUploader(field, label, isSmall = false) {
        const { form } = this.state;
        const value = form[field];
        const loadingKey = field === 'imageUrl' ? 'uploadingImage' : 'uploadingLogo';
        const isUploading = this.state[loadingKey];
        const inputId = `upload-${field}`;

        if (isUploading) {
            return (
                <div className={s.formGroup}>
                    <label>{label}</label>
                    <div className={s.uploadLoading}>
                        <div className={s.spinner}></div>
                        Subiendo imagen...
                    </div>
                </div>
            );
        }

        if (value) {
            return (
                <div className={s.formGroup}>
                    <label>{label}</label>
                    <div className={s.uploadPreview}>
                        <img
                            src={value}
                            className={cx(s.uploadPreviewImg, isSmall && s.uploadPreviewSmall)}
                            alt={label}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <button
                            type="button"
                            className={s.uploadRemoveBtn}
                            onClick={() => this.handleFormChange(field, '')}
                            title="Eliminar imagen"
                        >
                            <X size={12} />
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className={s.formGroup}>
                <label>{label}</label>
                <div
                    className={s.uploadZone}
                    onClick={() => document.getElementById(inputId).click()}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add(s.uploadZoneActive); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove(s.uploadZoneActive); }}
                    onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove(s.uploadZoneActive);
                        const file = e.dataTransfer.files[0];
                        if (file) this.handleImageUpload(field, file);
                    }}
                >
                    <Upload size={24} className={s.uploadIcon} />
                    <p className={s.uploadText}>Click o arrastra una imagen</p>
                    <p className={s.uploadTextSmall}>JPG, PNG o WebP</p>
                    <input
                        id={inputId}
                        type="file"
                        className={s.uploadInput}
                        accept="image/jpeg,image/png,image/webp,image/jpg"
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) this.handleImageUpload(field, file);
                            e.target.value = '';
                        }}
                    />
                </div>
            </div>
        );
    }

    handleSave = async () => {
        const { form, editingAd } = this.state;
        const { client } = this.props;

        try {
            if (editingAd) {
                // Update
                const { data } = await client.mutate({
                    mutation: gql`
            mutation UpdateAppAd(
              $id: Int!, $title: String, $description: String, $imageUrl: String,
              $targetUrl: String, $placement: String, $locationId: Int, $priority: Int,
              $isActive: Boolean, $startDate: String, $endDate: String,
              $advertiserName: String, $advertiserLogo: String, $categoryType: String,
              $budget: Float, $costPerClick: Float, $costPerImpression: Float
            ) {
              updateAppAd(
                id: $id, title: $title, description: $description, imageUrl: $imageUrl,
                targetUrl: $targetUrl, placement: $placement, locationId: $locationId,
                priority: $priority, isActive: $isActive, startDate: $startDate,
                endDate: $endDate, advertiserName: $advertiserName, advertiserLogo: $advertiserLogo,
                categoryType: $categoryType, budget: $budget, costPerClick: $costPerClick,
                costPerImpression: $costPerImpression
              ) {
                status
                errorMessage
              }
            }
          `,
                    variables: {
                        id: editingAd,
                        ...form,
                        locationId: form.locationId ? parseInt(form.locationId) : null,
                        priority: parseInt(form.priority),
                        budget: parseFloat(form.budget),
                        costPerClick: parseFloat(form.costPerClick),
                        costPerImpression: parseFloat(form.costPerImpression),
                        startDate: form.startDate || null,
                        endDate: form.endDate || null,
                    }
                });

                if (data.updateAppAd.status === 200) {
                    this.showMessage('Campana actualizada exitosamente');
                    this.closeModal();
                    this.props.adsData.refetch();
                } else {
                    this.showMessage(data.updateAppAd.errorMessage || 'Error al actualizar', 'error');
                }
            } else {
                // Create
                const { data } = await client.mutate({
                    mutation: gql`
            mutation CreateAppAd(
              $title: String, $description: String, $imageUrl: String,
              $targetUrl: String, $placement: String, $locationId: Int, $priority: Int,
              $isActive: Boolean, $startDate: String, $endDate: String,
              $advertiserName: String, $advertiserLogo: String, $categoryType: String,
              $budget: Float, $costPerClick: Float, $costPerImpression: Float
            ) {
              createAppAd(
                title: $title, description: $description, imageUrl: $imageUrl,
                targetUrl: $targetUrl, placement: $placement, locationId: $locationId,
                priority: $priority, isActive: $isActive, startDate: $startDate,
                endDate: $endDate, advertiserName: $advertiserName, advertiserLogo: $advertiserLogo,
                categoryType: $categoryType, budget: $budget, costPerClick: $costPerClick,
                costPerImpression: $costPerImpression
              ) {
                id
                title
              }
            }
          `,
                    variables: {
                        ...form,
                        locationId: form.locationId ? parseInt(form.locationId) : null,
                        priority: parseInt(form.priority),
                        budget: parseFloat(form.budget),
                        costPerClick: parseFloat(form.costPerClick),
                        costPerImpression: parseFloat(form.costPerImpression),
                        startDate: form.startDate || null,
                        endDate: form.endDate || null,
                    }
                });

                if (data.createAppAd && data.createAppAd.id) {
                    this.showMessage('Campana creada exitosamente');
                    this.closeModal();
                    this.props.adsData.refetch();
                } else {
                    this.showMessage('Error al crear la campana', 'error');
                }
            }
        } catch (err) {
            this.showMessage('Error: ' + (err.message || 'Desconocido'), 'error');
        }
    };

    handleDelete = async (id) => {
        if (!confirm('Estas seguro de eliminar esta campana? Se eliminaran todos los datos de analytics asociados.')) return;

        const { client } = this.props;
        try {
            const { data } = await client.mutate({
                mutation: gql`
          mutation DeleteAppAd($id: Int!) {
            deleteAppAd(id: $id) { status errorMessage }
          }
        `,
                variables: { id }
            });

            if (data.deleteAppAd.status === 200) {
                this.showMessage('Campana eliminada');
                this.props.adsData.refetch();
            } else {
                this.showMessage(data.deleteAppAd.errorMessage || 'Error', 'error');
            }
        } catch (err) {
            this.showMessage('Error: ' + err.message, 'error');
        }
    };

    handleToggleActive = async (ad) => {
        const { client } = this.props;
        try {
            await client.mutate({
                mutation: gql`
          mutation UpdateAppAd($id: Int!, $isActive: Boolean) {
            updateAppAd(id: $id, isActive: $isActive) { status }
          }
        `,
                variables: { id: ad.id, isActive: !ad.isActive }
            });
            this.props.adsData.refetch();
        } catch (err) {
            this.showMessage('Error: ' + err.message, 'error');
        }
    };

    getPlacementLabel = (placement) => {
        const labels = {
            'home_explore': 'Home Explore',
            'popular_location': 'Popular Location',
            'listing_detail': 'Listing Detail',
        };
        return labels[placement] || placement;
    };

    getPlacementBadgeClass = (placement) => {
        const classes = {
            'home_explore': s.badgeExplore,
            'popular_location': s.badgeLocation,
            'listing_detail': s.badgeListing,
        };
        return classes[placement] || '';
    };

    renderCampaigns() {
        const { adsData } = this.props;
        const ads = adsData && adsData.getAppAds ? adsData.getAppAds : [];

        if (adsData && adsData.loading) {
            return <div className={s.loading}>Cargando campanas...</div>;
        }

        // Summary stats
        const totalImpressions = ads.reduce((sum, ad) => sum + (ad.totalImpressions || 0), 0);
        const totalClicks = ads.reduce((sum, ad) => sum + (ad.totalClicks || 0), 0);
        const avgCtr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';
        const totalSpend = ads.reduce((sum, ad) => sum + (ad.totalSpend || 0), 0);

        return (
            <div>
                {/* Quick Stats */}
                <div className={s.statsGrid}>
                    <div className={cx(s.statCard, s.statCardBlue)}>
                        <p className={s.statLabel}>Total Impresiones</p>
                        <p className={s.statValue}>{totalImpressions.toLocaleString()}</p>
                    </div>
                    <div className={cx(s.statCard, s.statCardGreen)}>
                        <p className={s.statLabel}>Total Clicks</p>
                        <p className={s.statValue}>{totalClicks.toLocaleString()}</p>
                    </div>
                    <div className={cx(s.statCard, s.statCardPurple)}>
                        <p className={s.statLabel}>CTR Promedio</p>
                        <p className={s.statValue}>{avgCtr}%</p>
                    </div>
                    <div className={cx(s.statCard, s.statCardOrange)}>
                        <p className={s.statLabel}>Revenue Total</p>
                        <p className={s.statValue}>${totalSpend.toFixed(2)}</p>
                    </div>
                </div>

                {ads.length === 0 ? (
                    <div className={s.emptyState}>
                        <Megaphone size={48} style={{ color: '#9ca3af', marginBottom: 12 }} />
                        <h3>No hay campanas publicitarias</h3>
                        <p>Crea tu primera campana para empezar a generar ingresos con publicidad</p>
                        <button className={cx(s.btn, s.btnPrimary)} onClick={this.openCreateModal}>
                            <Plus size={18} /> Nueva Campana
                        </button>
                    </div>
                ) : (
                    <div className={s.tableContainer}>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th>Imagen</th>
                                    <th>Titulo / Anunciante</th>
                                    <th>Placement</th>
                                    <th>Estado</th>
                                    <th>Impresiones</th>
                                    <th>Clicks</th>
                                    <th>CTR</th>
                                    <th>Revenue</th>
                                    <th>Activo</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ads.map(ad => (
                                    <tr key={ad.id}>
                                        <td>
                                            {ad.imageUrl ? (
                                                <img src={ad.imageUrl} className={s.adImage} alt={ad.title} />
                                            ) : (
                                                <div className={s.noImage}>Sin imagen</div>
                                            )}
                                        </td>
                                        <td>
                                            <strong>{ad.title}</strong>
                                            <br />
                                            <span style={{ fontSize: 12, color: '#6b7280' }}>{ad.advertiserName}</span>
                                        </td>
                                        <td>
                                            <span className={cx(s.badge, this.getPlacementBadgeClass(ad.placement))}>
                                                {this.getPlacementLabel(ad.placement)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={cx(s.badge, ad.isScheduleActive ? s.badgeActive : s.badgeInactive)}>
                                                {ad.isScheduleActive ? 'Activo' : 'Programado'}
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: 600 }}>
                                            {(ad.totalImpressions || 0).toLocaleString()}
                                        </td>
                                        <td style={{ fontWeight: 600 }}>
                                            {(ad.totalClicks || 0).toLocaleString()}
                                        </td>
                                        <td>
                                            <span className={cx(s.ctrBadge,
                                                ad.ctr >= 3 ? s.ctrHigh : ad.ctr >= 1 ? s.ctrMedium : s.ctrLow
                                            )}>
                                                {(ad.ctr || 0).toFixed(2)}%
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: 600 }}>
                                            ${(ad.totalSpend || 0).toFixed(2)}
                                        </td>
                                        <td>
                                            <button
                                                className={cx(s.toggle, ad.isActive && s.toggleActive)}
                                                onClick={() => this.handleToggleActive(ad)}
                                            >
                                                <div className={s.toggleKnob}></div>
                                            </button>
                                        </td>
                                        <td>
                                            <div className={s.actions}>
                                                <button className={cx(s.btn, s.btnEdit)} onClick={() => this.openEditModal(ad)}>
                                                    Editar
                                                </button>
                                                <button className={cx(s.btn, s.btnDanger)} onClick={() => this.handleDelete(ad.id)}>
                                                    Eliminar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    renderAnalytics() {
        const { analyticsData } = this.props;
        const analytics = analyticsData && analyticsData.getAppAdAnalytics;

        if (analyticsData && analyticsData.loading) {
            return <div className={s.loading}>Cargando analytics...</div>;
        }

        if (!analytics) {
            return (
                <div className={s.emptyState}>
                    <TrendingUp size={48} style={{ color: '#9ca3af', marginBottom: 12 }} />
                    <h3>Sin datos de analytics</h3>
                    <p>Los datos apareceran cuando las campanas empiecen a recibir impresiones y clicks</p>
                </div>
            );
        }

        const platformColors = {
            ios: '#007AFF',
            android: '#3DDC84',
            web: '#F59E0B'
        };

        // Calculate max for bar chart scaling
        const dailyStats = analytics.dailyStats || [];
        const maxImpressions = Math.max(...dailyStats.map(d => d.impressions), 1);

        return (
            <div>
                {/* Summary Cards */}
                <div className={s.statsGrid}>
                    <div className={cx(s.statCard, s.statCardBlue)}>
                        <p className={s.statLabel}>Total Impresiones</p>
                        <p className={s.statValue}>{(analytics.totalImpressions || 0).toLocaleString()}</p>
                    </div>
                    <div className={cx(s.statCard, s.statCardGreen)}>
                        <p className={s.statLabel}>Total Clicks</p>
                        <p className={s.statValue}>{(analytics.totalClicks || 0).toLocaleString()}</p>
                    </div>
                    <div className={cx(s.statCard, s.statCardPurple)}>
                        <p className={s.statLabel}>CTR Promedio</p>
                        <p className={s.statValue}>{(analytics.averageCtr || 0).toFixed(2)}%</p>
                    </div>
                    <div className={cx(s.statCard, s.statCardOrange)}>
                        <p className={s.statLabel}>Revenue Total</p>
                        <p className={s.statValue}>${(analytics.totalRevenue || 0).toFixed(2)}</p>
                    </div>
                </div>

                <div className={s.chartGrid}>
                    {/* Daily Stats Chart */}
                    <div className={s.chartSection}>
                        <h3 className={s.chartTitle}>Impresiones vs Clicks (Ultimos 30 dias)</h3>
                        <div className={s.legend}>
                            <div className={s.legendItem}>
                                <div className={s.legendDot} style={{ background: '#3b82f6' }}></div>
                                Impresiones
                            </div>
                            <div className={s.legendItem}>
                                <div className={s.legendDot} style={{ background: '#10b981' }}></div>
                                Clicks
                            </div>
                        </div>
                        {dailyStats.length > 0 ? (
                            <div className={s.barChart}>
                                {dailyStats.slice(-14).map((day, idx) => (
                                    <div className={s.barGroup} key={idx}>
                                        <div
                                            className={cx(s.bar, s.barImpressions)}
                                            style={{ height: `${(day.impressions / maxImpressions) * 160}px` }}
                                            title={`${day.impressions} impresiones`}
                                        ></div>
                                        <div
                                            className={cx(s.bar, s.barClicks)}
                                            style={{ height: `${Math.max((day.clicks / maxImpressions) * 160, 2)}px` }}
                                            title={`${day.clicks} clicks`}
                                        ></div>
                                        <div className={s.barLabel}>{day.date.split('-').slice(1).join('/')}</div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>Sin datos aun</p>
                        )}
                    </div>

                    {/* Platform Breakdown */}
                    <div className={s.chartSection}>
                        <h3 className={s.chartTitle}>Distribucion por Plataforma</h3>
                        <div className={s.platformList}>
                            {(analytics.platformBreakdown || []).map((platform, idx) => (
                                <div className={s.platformItem} key={idx}>
                                    <div className={s.platformDot} style={{ background: platformColors[platform.platform] || '#6b7280' }}></div>
                                    <div className={s.platformInfo}>
                                        <div className={s.platformName}>
                                            {platform.platform === 'ios' ? 'iOS' : platform.platform === 'android' ? 'Android' : 'Web'}
                                        </div>
                                        <div className={s.platformBar}>
                                            <div
                                                className={s.platformBarFill}
                                                style={{
                                                    width: `${platform.percentage}%`,
                                                    background: platformColors[platform.platform] || '#6b7280'
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                    <div className={s.platformPercent}>{platform.percentage}%</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Top Ads by CTR */}
                <div className={s.chartSection}>
                    <h3 className={s.chartTitle}>Top Campanas por CTR</h3>
                    {(analytics.topAdsByCtr || []).length > 0 ? (
                        <div className={s.tableContainer}>
                            <table className={s.table}>
                                <thead>
                                    <tr>
                                        <th>Campana</th>
                                        <th>Impresiones</th>
                                        <th>Clicks</th>
                                        <th>CTR</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {analytics.topAdsByCtr.map((ad, idx) => (
                                        <tr key={idx}>
                                            <td><strong>{ad.title}</strong></td>
                                            <td>{ad.impressions.toLocaleString()}</td>
                                            <td>{ad.clicks.toLocaleString()}</td>
                                            <td>
                                                <span className={cx(s.ctrBadge, ad.ctr >= 3 ? s.ctrHigh : ad.ctr >= 1 ? s.ctrMedium : s.ctrLow)}>
                                                    {ad.ctr.toFixed(2)}%
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p style={{ color: '#9ca3af', textAlign: 'center', padding: 20 }}>Sin datos suficientes</p>
                    )}
                </div>
            </div>
        );
    }

    renderModal() {
        const { showModal, editingAd, form } = this.state;
        if (!showModal) return null;

        return (
            <div className={s.modalOverlay} onClick={this.closeModal}>
                <div className={s.modal} onClick={e => e.stopPropagation()}>
                    <div className={s.modalHeader}>
                        <h2>{editingAd ? 'Editar Campana' : 'Nueva Campana'}</h2>
                        <button className={s.modalClose} onClick={this.closeModal}>
                            <X size={18} />
                        </button>
                    </div>

                    <div className={s.modalBody}>
                        <div className={s.formGrid}>
                            <div className={cx(s.formGroup, s.formGridFull)}>
                                <label>Titulo de la Campana *</label>
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={(e) => this.handleFormChange('title', e.target.value)}
                                    placeholder="Ej: Promocion Verano Marina Bay"
                                />
                            </div>

                            <div className={s.formGroup}>
                                <label>Nombre del Anunciante *</label>
                                <input
                                    type="text"
                                    value={form.advertiserName}
                                    onChange={(e) => this.handleFormChange('advertiserName', e.target.value)}
                                    placeholder="Ej: Marina Bay Resort"
                                />
                            </div>

                            {this.renderImageUploader('advertiserLogo', 'Logo del Anunciante', true)}

                            <div className={cx(s.formGridFull)}>
                                {this.renderImageUploader('imageUrl', 'Imagen del Anuncio')}
                            </div>

                            <div className={cx(s.formGroup, s.formGridFull)}>
                                <label>Link de Redireccion (Target URL) *</label>
                                <input
                                    type="text"
                                    value={form.targetUrl}
                                    onChange={(e) => this.handleFormChange('targetUrl', e.target.value)}
                                    placeholder="https://ejemplo.com/oferta"
                                />
                            </div>

                            <div className={cx(s.formGroup, s.formGridFull)}>
                                <label>Descripcion</label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => this.handleFormChange('description', e.target.value)}
                                    placeholder="Descripcion del anuncio..."
                                />
                            </div>

                            <div className={s.formGroup}>
                                <label>Placement *</label>
                                <select
                                    value={form.placement}
                                    onChange={(e) => this.handleFormChange('placement', e.target.value)}
                                >
                                    <option value="home_explore">Home Explore</option>
                                    <option value="popular_location">Popular Location</option>
                                    <option value="listing_detail">Listing Detail</option>
                                </select>
                            </div>

                            <div className={s.formGroup}>
                                <label>Categoria</label>
                                <select
                                    value={form.categoryType}
                                    onChange={(e) => this.handleFormChange('categoryType', e.target.value)}
                                >
                                    <option value="all">Todas</option>
                                    <option value="boat">Boats</option>
                                    <option value="posada">Posadas</option>
                                    <option value="experience">Experiencias</option>
                                </select>
                            </div>

                            {form.placement === 'popular_location' && (
                                <div className={s.formGroup}>
                                    <label>Popular Location</label>
                                    <select
                                        value={form.locationId}
                                        onChange={(e) => this.handleFormChange('locationId', e.target.value)}
                                    >
                                        <option value="">Seleccionar ubicacion...</option>
                                        {this.props.locationsData && this.props.locationsData.getPopularLocation &&
                                            this.props.locationsData.getPopularLocation.map(loc => (
                                                <option key={loc.id} value={loc.id}>{loc.location}</option>
                                            ))
                                        }
                                    </select>
                                </div>
                            )}

                            <div className={s.formGroup}>
                                <label>Prioridad</label>
                                <input
                                    type="number"
                                    value={form.priority}
                                    onChange={(e) => this.handleFormChange('priority', e.target.value)}
                                    placeholder="Mayor = se muestra primero"
                                />
                            </div>

                            <div className={s.formGroup}>
                                <label>Fecha de Inicio</label>
                                <input
                                    type="date"
                                    value={form.startDate}
                                    onChange={(e) => this.handleFormChange('startDate', e.target.value)}
                                />
                            </div>

                            <div className={s.formGroup}>
                                <label>Fecha de Fin</label>
                                <input
                                    type="date"
                                    value={form.endDate}
                                    onChange={(e) => this.handleFormChange('endDate', e.target.value)}
                                />
                            </div>

                            <div className={s.formGroup}>
                                <label>Presupuesto ($)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={form.budget}
                                    onChange={(e) => this.handleFormChange('budget', e.target.value)}
                                />
                            </div>

                            <div className={s.formGroup}>
                                <label>Costo por Click (CPC $)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={form.costPerClick}
                                    onChange={(e) => this.handleFormChange('costPerClick', e.target.value)}
                                />
                            </div>

                            <div className={s.formGroup}>
                                <label>Costo por 1000 Impresiones (CPM $)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={form.costPerImpression}
                                    onChange={(e) => this.handleFormChange('costPerImpression', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={s.modalFooter}>
                        <button className={cx(s.btn, s.btnSmall)} onClick={this.closeModal} style={{ background: '#f3f4f6', color: '#374151' }}>
                            Cancelar
                        </button>
                        <button className={cx(s.btn, s.btnPrimary)} onClick={this.handleSave}>
                            {editingAd ? 'Guardar Cambios' : 'Crear Campana'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { activeTab, message, messageType } = this.state;

        return (
            <div className={s.container}>
                {/* Header */}
                <div className={s.header}>
                    <div className={s.headerLeft}>
                        <h1><Megaphone size={28} style={{ marginRight: 12, verticalAlign: 'middle' }} />Publicidad (App Ads)</h1>
                        <p>Gestiona las campanas publicitarias de la app. Crea anuncios, configura placements y monitorea el rendimiento.</p>
                    </div>
                    <button className={cx(s.btn, s.btnPrimary)} onClick={this.openCreateModal}>
                        <Plus size={18} /> Nueva Campana
                    </button>
                </div>

                {/* Messages */}
                {message && (
                    <div className={messageType === 'error' ? s.errorMsg : s.successMsg}>
                        {message}
                    </div>
                )}

                {/* Tabs */}
                <div className={s.tabs}>
                    <button
                        className={cx(s.tab, activeTab === 'campaigns' && s.tabActive)}
                        onClick={() => this.setState({ activeTab: 'campaigns' })}
                    >
                        <Megaphone size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                        Campanas
                    </button>
                    <button
                        className={cx(s.tab, activeTab === 'analytics' && s.tabActive)}
                        onClick={() => this.setState({ activeTab: 'analytics' })}
                    >
                        <TrendingUp size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                        Analytics Dashboard
                    </button>
                </div>

                {/* Content */}
                {activeTab === 'campaigns' ? this.renderCampaigns() : this.renderAnalytics()}

                {/* Modal */}
                {this.renderModal()}
            </div>
        );
    }
}

export default compose(
    withStyles(s),
    withApollo,
    graphql(GET_APP_ADS, {
        name: 'adsData',
        options: {
            fetchPolicy: 'network-only'
        }
    }),
    graphql(GET_ANALYTICS, {
        name: 'analyticsData',
        options: {
            fetchPolicy: 'network-only'
        }
    }),
    graphql(GET_POPULAR_LOCATIONS, {
        name: 'locationsData',
        options: {
            fetchPolicy: 'network-only'
        }
    }),
)(AppAds);
